import { v4 as uuidv4 } from 'uuid';
import { toast } from 'sonner';
import { storage } from '@/lib/auth';
import { authBus } from '@/lib/auth-bus';
import { IS_CN_SITE } from '@/config/site';
import { SENTRY_API_INFO_LOGS_ENABLED } from '@/config/sentry';
import { normalizeLanguage } from '@/lib/i18n/types';
import { logSentryError, logSentryInfo, logSentryWarn } from '@/lib/sentryLogs';
import { reportApiErrorToSentry } from '@/lib/sentryReporting';

type RequestOptions = Omit<RequestInit, 'headers'> & {
  headers?: Record<string, string>;
  /** 静默模式：接口报错时不弹出 toast */
  silent?: boolean;
};

export class ApiRequestError extends Error {
  code?: string | number;
  status?: number;
  data?: unknown;

  constructor(message: string, options?: { code?: string | number; status?: number; data?: unknown }) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = options?.code;
    this.status = options?.status;
    this.data = options?.data;
  }
}

function isUnauthorizedError(code?: string | number, message?: string, status?: number): boolean {
  return code === 'UNAUTHORIZED'
    || (code === 'AUTH_REQUIRED' && status === 401)
    || message?.includes('Authorization') === true;
}

function shouldReportApiErrorToSentry(error: ApiRequestError): boolean {
  return !(
    isUnauthorizedError(error.code, error.message, error.status)
    || error.code === 'INVALID_INVITATION'
  );
}

const shouldAttachTraceId = !IS_CN_SITE;

function normalizeApiLogUrl(url: string) {
  if (typeof window === 'undefined') {
    return url;
  }

  try {
    return new URL(url, window.location.origin).toString();
  } catch {
    return url;
  }
}

function getApiLogPath(url: string) {
  try {
    const parsedUrl = url.startsWith('http://') || url.startsWith('https://')
      ? new URL(url)
      : new URL(url, 'http://localhost');

    return `${parsedUrl.pathname}${parsedUrl.search}`;
  } catch {
    return url;
  }
}

function buildApiLogMessage(
  method: string,
  url: string,
  stage:
    | 'request_started'
    | 'network_failed'
    | 'parse_failed'
    | 'response_received'
    | 'http_error'
    | 'business_error',
  status?: number,
) {
  const apiLabel = `${method.toUpperCase()} ${getApiLogPath(url)}`;

  switch (stage) {
    case 'request_started':
      return `${apiLabel} request started`;
    case 'network_failed':
      return `${apiLabel} network failed`;
    case 'parse_failed':
      return `${apiLabel} response parse failed`;
    case 'response_received':
      return `${apiLabel} response received${status ? ` (${status})` : ''}`;
    case 'http_error':
      return `${apiLabel} HTTP error${status ? ` (${status})` : ''}`;
    case 'business_error':
      return `${apiLabel} business error${status ? ` (${status})` : ''}`;
    default:
      return apiLabel;
  }
}

function sanitizeHeaderValue(key: string, value: string) {
  const normalizedKey = key.toLowerCase();
  if (normalizedKey === 'authorization') {
    return '[REDACTED]';
  }

  return value;
}

function sanitizeHeaders(headers: Record<string, string>) {
  return Object.fromEntries(
    Object.entries(headers).map(([key, value]) => [key, sanitizeHeaderValue(key, value)]),
  );
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return Object.prototype.toString.call(value) === '[object Object]';
}

function getObjectString(value: Record<string, unknown>, key: string) {
  const field = value[key];
  return typeof field === 'string' ? field : undefined;
}

function getObjectCode(value: Record<string, unknown>) {
  const field = value.error_code ?? value.code;
  return typeof field === 'string' || typeof field === 'number' ? field : undefined;
}

function getObjectData(value: Record<string, unknown>) {
  return value.data;
}

const redactedApiLogKeys = new Set([
  // CLI 密钥签发响应里的明文密钥字段（/api/users/cli/keys），只出现一次，不得进日志
  'key',
  'client_secret',
  'pay_url',
  'code_url',
  'qr_code_url',
  'session_url',
  'signature',
  'secret',
  // Copilot 409/快照 body 里可能携带会话内容（C2-3）：interrupt 是 Copilot
  // 向用户提的问题原文、approval 携带报价与创作摘要——都属于用户会话隐私，
  // 不得随错误上报进 Sentry/日志。
  'interrupt',
  'approval',
]);

function sanitizeApiLogValue(
  value: unknown,
  seen = new WeakSet<object>(),
): unknown {
  if (value == null || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (value instanceof FormData) {
    return Array.from(value.entries()).map(([key, entryValue]) => ([
      key,
      typeof entryValue === 'string'
        ? entryValue
        : {
            name: entryValue.name,
            type: entryValue.type,
            size: entryValue.size,
          },
    ]));
  }

  if (Array.isArray(value)) {
    return value.map((item) => sanitizeApiLogValue(item, seen));
  }

  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
    };
  }

  if (typeof value === 'object') {
    if (seen.has(value)) {
      return '[Circular]';
    }

    seen.add(value);

    if (isPlainObject(value)) {
      return Object.fromEntries(
        Object.entries(value).map(([key, entryValue]) => {
          const normalizedKey = key.toLowerCase();
          if (
            redactedApiLogKeys.has(normalizedKey)
            || (
              normalizedKey === 'payload'
              && typeof value.type === 'string'
              && value.type.toLowerCase().includes('payment')
            )
            || (
              normalizedKey === 'payload'
              && typeof value.action_type === 'string'
              && value.action_type.toLowerCase().includes('payment')
            )
            || normalizedKey.includes('token')
            || normalizedKey.includes('authorization')
            || normalizedKey.includes('password')
          ) {
            return [key, '[REDACTED]'];
          }

          return [key, sanitizeApiLogValue(entryValue, seen)];
        }),
      );
    }

    return String(value);
  }

  return String(value);
}

function parseRequestBody(body: RequestInit['body']) {
  if (!body) {
    return undefined;
  }

  if (typeof body === 'string') {
    try {
      return sanitizeApiLogValue(JSON.parse(body));
    } catch {
      return body;
    }
  }

  if (body instanceof FormData) {
    return sanitizeApiLogValue(body);
  }

  return sanitizeApiLogValue(body);
}

function handleUnauthorizedError() {
  if (typeof window === 'undefined') {
    return;
  }

  const { pathname } = window.location;
  if (pathname !== '/' && !pathname.includes('/login')) {
    authBus.fireUnauthorized();
  }
}

export function shouldSuppressApiErrorToast(error: unknown): boolean {
  if (!(error instanceof ApiRequestError)) {
    return false;
  }

  return isUnauthorizedError(error.code, error.message, error.status) || error.code === 'INVALID_INVITATION';
}

async function getLanguageFromStorage(): Promise<string> {
  if (typeof window === 'undefined') return 'en';
  return normalizeLanguage(localStorage.getItem('language'));
}

export async function apiRequest<T = unknown>(
  url: string,
  options: RequestOptions = {}
): Promise<T> {
  const { silent, ...fetchOptions } = options;
  const language = await getLanguageFromStorage();
  const traceId = uuidv4();
  const method = fetchOptions.method ?? 'GET';
  const normalizedUrl = normalizeApiLogUrl(url);
  const apiPath = getApiLogPath(normalizedUrl);

  const defaultHeaders: Record<string, string> = {
    'Accept-Language': language,
    'Language': language,
  };

  if (shouldAttachTraceId) {
    defaultHeaders.Trace_id = traceId;
  }

  if (!(options.body instanceof FormData)) {
    defaultHeaders['Content-Type'] = 'application/json';
  }

  const authData = storage.getAuthData()
  if (authData.token) {
    defaultHeaders.Authorization = 'Bearer ' + authData.token;
  }

  const headers = {
    ...defaultHeaders,
    ...options.headers,
  };
  const sanitizedHeaders = sanitizeHeaders(headers);
  const requestBody = parseRequestBody(fetchOptions.body);
  const requestContext = {
    url: normalizedUrl,
    path: apiPath,
    method,
    headers: sanitizedHeaders,
    body: requestBody,
    credentials: 'include',
  };

  if (SENTRY_API_INFO_LOGS_ENABLED) {
    logSentryInfo(buildApiLogMessage(method, normalizedUrl, 'request_started'), {
      api_event: 'api.request.started',
      log_source: 'api_request',
      api_url: normalizedUrl,
      api_path: apiPath,
      api_method: method,
      api_trace_id: traceId,
      api_silent: Boolean(silent),
      request: requestContext,
    });
  }

  let response: Response;
  try {
    response = await fetch(url, {
      ...fetchOptions,
      headers,
      credentials: 'include',
    });
  } catch (error) {
    logSentryError(buildApiLogMessage(method, normalizedUrl, 'network_failed'), {
      api_event: 'api.request.network_failed',
      log_source: 'api_request',
      api_url: normalizedUrl,
      api_path: apiPath,
      api_method: method,
      api_trace_id: traceId,
      api_silent: Boolean(silent),
      request: requestContext,
      error: sanitizeApiLogValue(error),
    });
    reportApiErrorToSentry(error, {
      url: normalizedUrl,
      method,
      language,
      traceId,
      silent: Boolean(silent),
      request: requestContext,
    });
    throw error;
  }

  let data: unknown = null;
  const responseText = await response.text();
  if (responseText) {
    try {
      data = JSON.parse(responseText);
    } catch (error) {
      if (response.ok) {
        logSentryError(buildApiLogMessage(method, normalizedUrl, 'parse_failed', response.status), {
          api_event: 'api.response.parse_failed',
          log_source: 'api_request',
          api_url: normalizedUrl,
          api_path: apiPath,
          api_method: method,
          api_trace_id: traceId,
          api_silent: Boolean(silent),
          request: requestContext,
          response: {
            status: response.status,
            ok: response.ok,
          },
          error: sanitizeApiLogValue(error),
        });
        reportApiErrorToSentry(error, {
          url: normalizedUrl,
          method,
          language,
          traceId,
          silent: Boolean(silent),
          status: response.status,
          request: requestContext,
        });
        throw error;
      }

      const fallbackMessage = responseText.trim();
      data = {
        code: 'HTTP_ERROR',
        message: fallbackMessage && fallbackMessage.length <= 200
          ? fallbackMessage
          : `HTTP ${response.status}`,
      };
    }
  }

  const sanitizedResponseData = sanitizeApiLogValue(data);

  if (SENTRY_API_INFO_LOGS_ENABLED) {
    logSentryInfo(buildApiLogMessage(method, normalizedUrl, 'response_received', response.status), {
      api_event: 'api.response.received',
      log_source: 'api_request',
      api_url: normalizedUrl,
      api_path: apiPath,
      api_method: method,
      api_trace_id: traceId,
      api_silent: Boolean(silent),
      request: requestContext,
      response: {
        status: response.status,
        ok: response.ok,
        body: sanitizedResponseData,
      },
    });
  }

  if (!response.ok) {
    const error = isPlainObject(data) ? data : { code: 'ERROR', message: 'Request failed' };
    const errorCode = getObjectCode(error);
    const message = getObjectString(error, 'message') || getObjectString(error, 'error') || `HTTP ${response.status}`;
    if (isUnauthorizedError(errorCode, message, response.status)) {
      handleUnauthorizedError();
    } else if (errorCode === 'INVALID_INVITATION') {
      if (typeof window !== 'undefined') {
        authBus.fireInvalidInvitation(message);
      }
    } else if (!silent) {
      toast.error(message);
    }
    logSentryWarn(buildApiLogMessage(method, normalizedUrl, 'http_error', response.status), {
      api_event: 'api.response.http_error',
      log_source: 'api_request',
      api_url: normalizedUrl,
      api_path: apiPath,
      api_method: method,
      api_trace_id: traceId,
      api_silent: Boolean(silent),
      request: requestContext,
      response: {
        status: response.status,
        ok: response.ok,
        body: sanitizedResponseData,
      },
      error_code: errorCode ?? '',
      error_message: message,
    });
    const apiError = new ApiRequestError(message, { code: errorCode, status: response.status, data: getObjectData(error) });
    if (shouldReportApiErrorToSentry(apiError)) {
      reportApiErrorToSentry(apiError, {
        url: normalizedUrl,
        method,
        language,
        traceId,
        silent: Boolean(silent),
        status: response.status,
        code: errorCode,
        data: getObjectData(error),
        request: requestContext,
        response: {
          status: response.status,
          ok: response.ok,
          body: sanitizedResponseData,
        },
      });
    }
    throw apiError;
  }

  // 后端业务错误：HTTP 200 但 code 非成功码
  if (isPlainObject(data) && typeof data.code === 'number' && data.code !== 0 && data.code !== 200) {
    const message = getObjectString(data, 'message') || `Error code: ${data.code}`;
    if (isUnauthorizedError(data.code, message, response.status)) {
      handleUnauthorizedError();
    } else if (!silent) {
      toast.error(message);
    }
    logSentryWarn(buildApiLogMessage(method, normalizedUrl, 'business_error', response.status), {
      api_event: 'api.response.business_error',
      log_source: 'api_request',
      api_url: normalizedUrl,
      api_path: apiPath,
      api_method: method,
      api_trace_id: traceId,
      api_silent: Boolean(silent),
      request: requestContext,
      response: {
        status: response.status,
        ok: response.ok,
        body: sanitizedResponseData,
      },
      error_code: data.code,
      error_message: message,
    });
    const apiError = new ApiRequestError(message, { code: data.code, status: response.status, data: getObjectData(data) });
    if (shouldReportApiErrorToSentry(apiError)) {
      reportApiErrorToSentry(apiError, {
        url: normalizedUrl,
        method,
        language,
        traceId,
        silent: Boolean(silent),
        status: response.status,
        code: data.code,
        data: getObjectData(data),
        request: requestContext,
        response: {
          status: response.status,
          ok: response.ok,
          body: sanitizedResponseData,
        },
      });
    }
    throw apiError;
  }

  return data as T;
}

export function get<T = unknown>(
  url: string,
  options?: Omit<RequestOptions, 'method'>
): Promise<T> {
  return apiRequest<T>(url, { ...options, method: 'GET' });
}

export function post<T = unknown>(
  url: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(url, {
    ...options,
    method: 'POST',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function put<T = unknown>(
  url: string,
  body?: unknown,
  options?: Omit<RequestOptions, 'method' | 'body'>
): Promise<T> {
  return apiRequest<T>(url, {
    ...options,
    method: 'PUT',
    body: body ? JSON.stringify(body) : undefined,
  });
}

export function del<T = unknown>(
  url: string,
  options?: Omit<RequestOptions, 'method'>
): Promise<T> {
  return apiRequest<T>(url, { ...options, method: 'DELETE' });
}
